import React, { useEffect } from 'react'
import { IDefaultSpecialTerms, TSpecialTermsSchema } from '../../../../../api/types'
import { ErrorBoundary } from '@reapitform/utils'
import { SpecialConditionsForm } from '../special-conditions-contact'
import { useDefaultSpecialConditionUpdate } from '../../../../../api/defaults/special-terms'
import { Button, ButtonGroup } from '@reapit/elements'
import { Drawer } from '@reapitform/common-ui'

interface IUpdateSpecialConditionsModalProps {
  defaultValues?: IDefaultSpecialTerms
  isOpen: boolean
  onOpenChange: (isOpen: boolean) => void
  refreshList: (data: IDefaultSpecialTerms[] | ((preData: IDefaultSpecialTerms[]) => IDefaultSpecialTerms[])) => void
}

export const UpdateSpecialConditionsModal: React.FC<IUpdateSpecialConditionsModalProps> = ({
  isOpen,
  onOpenChange,
  refreshList,
  defaultValues,
}) => {
  const { isLoading, updateSpecialCondition, isSuccess, returnData } = useDefaultSpecialConditionUpdate()
  const formId: string = React.useMemo(() => 'form-add-repairers', [])

  const handleClose = () => onOpenChange(false)
  const handleConfirm = (data: TSpecialTermsSchema) => {
    if (!data.clauses) return
    if (!data.id) return
    const clauses: string[] = data.clauses.reduce((result: string[], currentClause) => {
      if (!currentClause.value || typeof currentClause.value !== 'string') return result
      return [...result, currentClause.value]
    }, [])

    updateSpecialCondition({
      id: data.id,
      title: data.title,
      useAsDefault: data.useAsDefault,
      clauses,
    })
  }

  useEffect(() => {
    if (!isSuccess || !returnData) return

    refreshList((prevList) =>
      prevList.map((specialCondition) => {
        if (specialCondition.id !== returnData.id) return specialCondition
        return returnData
      }),
    )
    handleClose()
  }, [isSuccess, returnData])

  const DrawerFooter = React.useCallback(() => {
    return (
      <ButtonGroup alignment="right">
        <Button intent="default" disabled={isLoading} onClick={handleClose}>
          Cancel
        </Button>
        <Button loading={isLoading} disabled={isLoading} intent="primary" type="submit" form={formId}>
          Update Template
        </Button>
      </ButtonGroup>
    )
  }, [isLoading, handleClose])

  if (!isOpen || !defaultValues) return null

  return (
    <ErrorBoundary>
      <Drawer
        isOpen={isOpen}
        title="Edit Template"
        canDismiss
        onDrawerClose={handleClose}
        footerItems={<DrawerFooter />}
      >
        <SpecialConditionsForm formId={formId} defaultValues={defaultValues} onSubmit={handleConfirm} />
      </Drawer>
    </ErrorBoundary>
  )
}
